import * as React from 'react'
import { useForm, useFieldArray } from 'react-hook-form'

import {
  SittingReportStep1 as Presenter,
  IProps as IPresenter,
} from './presenter'
import { useInitialize } from '@/hooks/useInitialize'
import {
  parseDateStringToJstDate,
  parseDate,
  dateToStringWithFormat,
} from '@/utils/dateFormat'
import {
  tmpSaveSittingReportStep1,
  buildReserveOption,
  getOptionAlert,
  getPriceInfoDetail,
} from '@/apis/reserves'

const DEFAULT_OPTION = {
  unit_type: 'time' as const,
}

interface IProps
  extends Omit<IPresenter, 'confirmApproveHandler' | 'confirmCancelHandler'> {
  reserve: IReserveSummary
  isTokyoBsWaiting?: boolean
  isTokyoBsTmpUse?: boolean
  isReportBack?: boolean
  actual_options: IOption[]
  additionalChildOption: IAdditionalChildOption
  priceDetailHtml: string
  nextUrl: string
  backUrl: string
}

const buildAdditionalChildOption = (
  option: IAdditionalChildOption,
): IAdditionalChildOptionState => {
  const date = parseDate(option.actual_apply_start_at)
  return {
    id: option.id,
    year: dateToStringWithFormat(date, 'yyyy'),
    month: dateToStringWithFormat(date, 'MM'),
    day: dateToStringWithFormat(date, 'dd'),
    hour: dateToStringWithFormat(date, 'HH'),
    minute: dateToStringWithFormat(date, 'mm'),
  }
}

const buildTmpSaveApiParams = (
  reserve: IReserveSummary,
  state: ICompleteReportFormStateStep1,
) => {
  const date1 = new Date(state.startDay)
  const startFormattedDate = `${date1.getFullYear()}-${String(
    date1.getMonth() + 1,
  ).padStart(2, '0')}-${String(date1.getDate()).padStart(2, '0')}`

  const date2 = new Date(state.endDay)
  const endFormattedDate = `${date2.getFullYear()}-${String(
    date2.getMonth() + 1,
  ).padStart(2, '0')}-${String(date2.getDate()).padStart(2, '0')}`

  reserve.actual_start_at = `${startFormattedDate} ${state.startHour}:${state.startMinute}`
  reserve.actual_end_at = `${endFormattedDate} ${state.endHour}:${state.endMinute}`
  reserve.actual_transportation_price = state.transportationPrice
  reserve.actual_transportation_price_description =
    state.transportationPriceDescription
  return reserve
}

const buildGetOptionAlertApiParams = (
  reserve: IReserveSummary,
  state: ICompleteReportFormStateStep1,
  defaultStartAt: Date,
  defaultEndAt: Date,
  isTokyoBsWaiting: boolean,
) => {
  return isTokyoBsWaiting
    ? {
        js_start_date_dummy: state.startDay
          ? dateToStringWithFormat(state.startDay, 'yyyy-MM-dd')
          : '',
        js_end_date_dummy: state.endDay
          ? dateToStringWithFormat(state.endDay, 'yyyy-MM-dd')
          : '',
        reserve: {
          id: reserve.id,
          'actual_start_at(1i)': dateToStringWithFormat(defaultStartAt, 'yyyy'),
          'actual_start_at(2i)': dateToStringWithFormat(defaultStartAt, 'MM'),
          'actual_start_at(3i)': dateToStringWithFormat(defaultStartAt, 'dd'),
          'actual_start_at(4i)': state.startHour,
          'actual_start_at(5i)': state.startMinute,
          'actual_end_at(1i)': dateToStringWithFormat(defaultEndAt, 'yyyy'),
          'actual_end_at(2i)': dateToStringWithFormat(defaultEndAt, 'MM'),
          'actual_end_at(3i)': dateToStringWithFormat(defaultEndAt, 'dd'),
          'actual_end_at(4i)': state.endHour,
          'actual_end_at(5i)': state.endMinute,
          additional_child_option_attributes: {
            id: state.additionalChildOption.id,
            'actual_apply_start_at(1i)': state.additionalChildOption.year,
            'actual_apply_start_at(2i)': state.additionalChildOption.month,
            'actual_apply_start_at(3i)': state.additionalChildOption.day,
            'actual_apply_start_at(4i)': state.additionalChildOption.hour,
            'actual_apply_start_at(5i)': state.additionalChildOption.minute,
          },
        },
      }
    : {
        js_start_date_dummy: state.startDay
          ? dateToStringWithFormat(state.startDay, 'yyyy-MM-dd')
          : '',
        js_end_date_dummy: state.endDay
          ? dateToStringWithFormat(state.endDay, 'yyyy-MM-dd')
          : '',
        reserve: {
          id: reserve.id,
          'actual_start_at(1i)': dateToStringWithFormat(defaultStartAt, 'yyyy'),
          'actual_start_at(2i)': dateToStringWithFormat(defaultStartAt, 'MM'),
          'actual_start_at(3i)': dateToStringWithFormat(defaultStartAt, 'dd'),
          'actual_start_at(4i)': state.startHour,
          'actual_start_at(5i)': state.startMinute,
          'actual_end_at(1i)': dateToStringWithFormat(defaultEndAt, 'yyyy'),
          'actual_end_at(2i)': dateToStringWithFormat(defaultEndAt, 'MM'),
          'actual_end_at(3i)': dateToStringWithFormat(defaultEndAt, 'dd'),
          'actual_end_at(4i)': state.endHour,
          'actual_end_at(5i)': state.endMinute,
        },
      }
}

const buildGetPriceInfoDetailApiParams = (
  reserve: IReserveSummary,
  state: ICompleteReportFormStateStep1,
  defaultStartAt: Date,
  defaultEndAt: Date,
  isTokyoBsWaiting: boolean,
) => {
  return isTokyoBsWaiting
    ? {
        js_start_date_dummy: state.startDay
          ? dateToStringWithFormat(state.startDay, 'yyyy-MM-dd')
          : '',
        js_end_date_dummy: state.endDay
          ? dateToStringWithFormat(state.endDay, 'yyyy-MM-dd')
          : '',
        reserve: {
          id: reserve.id,
          'actual_start_at(1i)': dateToStringWithFormat(defaultStartAt, 'yyyy'),
          'actual_start_at(2i)': dateToStringWithFormat(defaultStartAt, 'MM'),
          'actual_start_at(3i)': dateToStringWithFormat(defaultStartAt, 'dd'),
          'actual_start_at(4i)': state.startHour,
          'actual_start_at(5i)': state.startMinute,
          'actual_end_at(1i)': dateToStringWithFormat(defaultEndAt, 'yyyy'),
          'actual_end_at(2i)': dateToStringWithFormat(defaultEndAt, 'MM'),
          'actual_end_at(3i)': dateToStringWithFormat(defaultEndAt, 'dd'),
          'actual_end_at(4i)': state.endHour,
          'actual_end_at(5i)': state.endMinute,
          actual_options: state.options,
          actual_transportation_price: state.transportationPrice,
          additional_child_option_attributes: {
            id: state.additionalChildOption.id,
            'actual_apply_start_at(1i)': state.additionalChildOption.year,
            'actual_apply_start_at(2i)': state.additionalChildOption.month,
            'actual_apply_start_at(3i)': state.additionalChildOption.day,
            'actual_apply_start_at(4i)': state.additionalChildOption.hour,
            'actual_apply_start_at(5i)': state.additionalChildOption.minute,
          },
        },
      }
    : {
        js_start_date_dummy: state.startDay
          ? dateToStringWithFormat(state.startDay, 'yyyy-MM-dd')
          : '',
        js_end_date_dummy: state.endDay
          ? dateToStringWithFormat(state.endDay, 'yyyy-MM-dd')
          : '',
        reserve: {
          id: reserve.id,
          'actual_start_at(1i)': dateToStringWithFormat(defaultStartAt, 'yyyy'),
          'actual_start_at(2i)': dateToStringWithFormat(defaultStartAt, 'MM'),
          'actual_start_at(3i)': dateToStringWithFormat(defaultStartAt, 'dd'),
          'actual_start_at(4i)': state.startHour,
          'actual_start_at(5i)': state.startMinute,
          'actual_end_at(1i)': dateToStringWithFormat(defaultEndAt, 'yyyy'),
          'actual_end_at(2i)': dateToStringWithFormat(defaultEndAt, 'MM'),
          'actual_end_at(3i)': dateToStringWithFormat(defaultEndAt, 'dd'),
          'actual_end_at(4i)': state.endHour,
          'actual_end_at(5i)': state.endMinute,
          actual_options: state.options,
          actual_transportation_price: state.transportationPrice,
        },
      }
}

export const SittingReportStep1 = ({
  reserve,
  isTokyoBsWaiting,
  isTokyoBsTmpUse,
  isReportBack,
  actual_options,
  additionalChildOption,
  priceDetailHtml,
  nextUrl,
  backUrl,
}: IProps): JSX.Element => {
  const [isInitialized] = useInitialize()
  const [showToast, setShowToast] = React.useState<boolean>(false)
  const [showError, setShowError] = React.useState<boolean>(false)
  const [errorMessages, setErrorMessages] = React.useState<string[]>([])
  const [showOptionAlert, setShowOptionAlert] = React.useState<boolean>(false)
  const [priceDetail, setPriceDetail] = React.useState<string>(priceDetailHtml)
  const startAt = parseDateStringToJstDate(reserve.actual_start_at)
  const endAt = parseDateStringToJstDate(reserve.actual_end_at)
  const initOptions = actual_options.map((option) => buildReserveOption(option))
  const [isEdited, setIsEdited] = React.useState<boolean>(false)
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false)

  const {
    control,
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ICompleteReportFormStateStep1>({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      startDay: startAt,
      startHour: startAt.getHours().toString().padStart(2, '0'),
      startMinute: startAt.getMinutes().toString().padStart(2, '0'),
      endDay: endAt,
      endHour: endAt.getHours().toString().padStart(2, '0'),
      endMinute: endAt.getMinutes().toString().padStart(2, '0'),
      options: initOptions,
      additionalChildOption: !!additionalChildOption
        ? buildAdditionalChildOption(additionalChildOption)
        : undefined,
      transportationPrice: reserve.actual_transportation_price,
      transportationPriceDescription:
        reserve.actual_transportation_price_description,
    },
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options',
  })

  const inputStartDay = watch('startDay')
  const inputEndDay = watch('endDay')
  const inputAdditionalChildOption = watch('additionalChildOption')
  const inputStartHour = watch('startHour')
  const inputStartMinute = watch('startMinute')
  const inputEndHour = watch('endHour')
  const inputEndMinute = watch('endMinute')
  const inputOptions = watch('options')
  const inputTransportationPrice = watch('transportationPrice')
  const inputTransportationPriceDescription = watch(
    'transportationPriceDescription',
  )

  /*
   * トースト表示
   */
  const showErrorToast = (messages: string[]) => {
    setErrorMessages(messages)
    setTimeout(() => {
      setShowError(false)
      setErrorMessages([])
    }, 2000),
      setShowError(true)
  }

  /*
   * 送信
   */
  const isValid = async () => {
    const result = await tmpSaveSittingReportStep1(
      buildTmpSaveApiParams(reserve, watch()),
      watch('options'),
      isTokyoBsWaiting,
      true,
      inputAdditionalChildOption,
      isReportBack
        ? reserve.tmp_message_on_report_back_long
        : reserve.message_on_report_input_long,
      reserve.message_on_report_back,
    )
    if (result.result && result.res.result) {
      window.location.href = nextUrl
    } else {
      showErrorToast(result.res.errors)
      window.scroll({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const isInValid = () => {
    showErrorToast(['エラーを確認してください'])
    window.scroll({
      top: 0,
      behavior: 'smooth',
    })
  }

  /*
   * 前の画面に戻る
   */
  const cancelHandler = () => {
    console.log('page back')
    if (isEdited) {
      setShowConfirm(true)
    } else {
      location.href = backUrl
    }
  }

  const confirmApproveHandler = () => {
    location.href = backUrl
  }

  const confirmCancelHandler = () => {
    setShowConfirm(false)
  }

  /*
   * 編集済みかのハンドリング
   */
  React.useEffect(() => {
    if (isInitialized) {
      setIsEdited(true)
    }
  }, [
    JSON.stringify(inputOptions),
    JSON.stringify(inputStartDay),
    inputStartHour,
    inputStartMinute,
    JSON.stringify(inputEndDay),
    inputEndHour,
    inputEndMinute,
    inputAdditionalChildOption,
    inputTransportationPrice,
    inputTransportationPriceDescription,
  ])

  /*
   * 一時保存
   */
  const clickTmpSaveHandler = async () => {
    const result = await tmpSaveSittingReportStep1(
      buildTmpSaveApiParams(reserve, watch()),
      watch('options'),
      isTokyoBsWaiting,
      false,
      inputAdditionalChildOption,
      isReportBack
        ? reserve.tmp_message_on_report_back_long
        : reserve.message_on_report_input_long,
      reserve.message_on_report_back,
    )
    if (result.result && result.res.result) {
      setIsEdited(false)
      setTimeout(() => {
        setShowToast(false)
      }, 2000),
        setShowToast(true)
    } else {
      showErrorToast(result.res.errors)
      window.scroll({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  /*
   * オプション料金
   */
  const addOptionHandler = () => {
    append(DEFAULT_OPTION, { shouldFocus: false })
  }

  const deleteOptionHandler = (index: number) => {
    remove(index)
  }

  /*
   * 料金表示
   */
  const changePriceHandler = async () => {
    const priceDetailParams = buildGetPriceInfoDetailApiParams(
      reserve,
      watch(),
      startAt,
      endAt,
      isTokyoBsWaiting,
    )
    const optionAlertParams = buildGetOptionAlertApiParams(
      reserve,
      watch(),
      startAt,
      endAt,
      isTokyoBsWaiting,
    )
    const results = await Promise.all([
      getPriceInfoDetail(reserve, priceDetailParams),
      getOptionAlert(reserve, optionAlertParams),
    ])
    // 料金表示
    if (results[0].result) {
      setPriceDetail(results[0].res)
    }
    // 時間変更アラート
    if (results[1].result) {
      setShowOptionAlert(results[1].res.result)
    }
  }

  return (
    <Presenter
      isInitialized={isInitialized}
      isTokyoBsWaiting={isTokyoBsWaiting}
      isReportBack={isReportBack}
      fields={fields}
      inputStartDay={inputStartDay}
      inputEndDay={inputEndDay}
      inputOptions={inputOptions}
      inputAdditionalChildOption={inputAdditionalChildOption}
      inputTransportationPrice={inputTransportationPrice}
      showToast={showToast}
      showError={showError}
      errorMessages={errorMessages}
      showOptionAlert={showOptionAlert}
      priceDetailHtml={priceDetail}
      control={control}
      errors={errors}
      register={register}
      submitHandler={handleSubmit(isValid, isInValid)}
      cancelHandler={cancelHandler}
      clickTmpSaveHandler={clickTmpSaveHandler}
      addOptionHandler={addOptionHandler}
      deleteOptionHandler={deleteOptionHandler}
      changePriceHandler={changePriceHandler}
      showConfirm={showConfirm}
      confirmApproveHandler={confirmApproveHandler}
      confirmCancelHandler={confirmCancelHandler}
    />
  )
}

export default SittingReportStep1
